/**
 * Constantes e enums da campanha Conexão Varga.
 * Regras de negócio completas: docs/REGRAS-PROGRAMA.md.
 * Valores editáveis pelo admin vivem na tabela `configuracao` (não aqui);
 * aqui ficam só os que são estruturais (chaves, enums, o ciclo de 4 níveis).
 */

/** Fim da vigência do programa (SPEC.md §12 / REGRAS §8). */
export const CAMPANHA_FIM = '2027-07-07';

/** O ciclo de brindes do indicador tem 4 níveis e reinicia. Sem teto. */
export const NIVEIS_CICLO = 4;

/**
 * Nível do brinde para a N-ésima indicação convertida (SPEC.md §5.1).
 * `conversoesAnteriores` = quantas o indicador já tinha ANTES desta.
 *   0 -> 1, 3 -> 4, 4 -> 1 (reinicia).
 */
export function nivelDoCiclo(conversoesAnteriores: number): number {
  return (conversoesAnteriores % NIVEIS_CICLO) + 1;
}

/** Papel do brinde numa `concessao` de `movimento_brinde`. */
export const PAPEIS_BRINDE = ['indicador', 'indicado'] as const;
export type PapelBrinde = (typeof PAPEIS_BRINDE)[number];

/** Tipos de evento de `movimento_brinde` (append-only; voucher não expira). */
export const TIPOS_MOVIMENTO_BRINDE = ['concessao', 'entrega'] as const;
export type TipoMovimentoBrinde = (typeof TIPOS_MOVIMENTO_BRINDE)[number];

/** Status derivado de um voucher (não é coluna — vem da view `brindes_cliente`). */
export const STATUS_VOUCHER = ['disponivel', 'entregue'] as const;
export type StatusVoucher = (typeof STATUS_VOUCHER)[number];

/** Ator de uma escrita auditável (SPEC.md §4 AuditLog). */
export const ATORES = ['humano', 'agente', 'sistema'] as const;
export type Ator = (typeof ATORES)[number];

/** Quem participa do programa (SPEC.md §4 Cliente). */
export const TIPOS_PARTICIPANTE = ['cliente', 'funcionario'] as const;
export type TipoParticipante = (typeof TIPOS_PARTICIPANTE)[number];

/** Chaves da tabela `configuracao` (SPEC.md §4). */
export const CHAVES_CONFIGURACAO = [
  'campanha_inicio',
  'campanha_fim',
  'brinde_indicado_descricao',
  'termos_campanha_texto',
  'loja_bairro',
  'loja_endereco',
  'loja_horario',
  'loja_whatsapp',
  'loja_descricao_curta', // "uma linha do que a Varga faz bem" (UX §6 / landing)
  'validade_codigo_dias',
] as const;
export type ChaveConfiguracao = (typeof CHAVES_CONFIGURACAO)[number];

/**
 * Subconjunto de `configuracao` exposto sem autenticação
 * (`GET /publico/configuracao`) — para o `AvisoCampanha` e a landing do convite.
 * O nome da loja vem de `EMPRESA_NOME` (env), não daqui — fonte única.
 */
export const CHAVES_CONFIGURACAO_PUBLICAS = [
  'campanha_fim',
  'termos_campanha_texto',
  'brinde_indicado_descricao',
  'loja_bairro',
  'loja_endereco',
  'loja_horario',
  'loja_whatsapp',
  'loja_descricao_curta',
] as const satisfies ReadonlyArray<ChaveConfiguracao>;

/** Tipos de notificação enfileirada (SPEC.md §4). */
export const TIPOS_NOTIFICACAO = [
  'boas_vindas',
  'indicacao_convertida',
  'recuperacao_senha',
  'quase_la',
] as const;
export type TipoNotificacao = (typeof TIPOS_NOTIFICACAO)[number];

/** Eventos de engajamento do portal — sem PII (SPEC.md §4, UX §10). */
export const TIPOS_EVENTO_PORTAL = [
  'home_visita',
  'codigo_copiado',
  'share_iniciado',
  'share_fallback',
  'convite_visita',
] as const;
export type TipoEventoPortal = (typeof TIPOS_EVENTO_PORTAL)[number];

/**
 * Seed dos 4 níveis de `recompensa_indicacao` (REGRAS §6). Editável pelo admin
 * depois — isto é só o valor inicial.
 */
export const RECOMPENSAS_INDICADOR_SEED: ReadonlyArray<{ nivel: number; descricao: string }> = [
  { nivel: 1, descricao: 'Kit Cuidado Automotivo' },
  { nivel: 2, descricao: 'Higienização do Ar-Condicionado' },
  { nivel: 3, descricao: 'Vitrificação do Para-Brisa' },
  { nivel: 4, descricao: 'Suporte de Celular + Lixeirinha' },
];

/** Brinde fixo do indicado (REGRAS §5). Seed de `configuracao.brinde_indicado_descricao`. */
export const BRINDE_INDICADO_SEED = 'Cristalização do vidro';
